import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MonthYearPicker } from "../components/Filters";
import { MONTHS } from "../lib/format";
import { Toaster } from "sonner";
import { Card } from "../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { School, Users, CalendarCheck, Wallet, Lock, KeyRound, Receipt, TrendingDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatRupiah } from "../lib/format";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-5 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          <p className="font-nunito font-extrabold text-2xl text-gtsnavy mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(8);
  const [data, setData] = useState(null);
  const [fin, setFin] = useState(null);

  useEffect(() => {
    api.get(`/dashboard?year=${year}&month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [year, month]);

  const isBM = user?.role === "branch_manager";

  useEffect(() => {
    if (isBM) api.get(`/finance/summary?year=${year}&month=${month}`).then((r) => setFin(r.data)).catch(() => {});
  }, [year, month, isBM]);

  const [charts, setCharts] = useState(null);
  useEffect(() => {
    if (isBM) api.get(`/dashboard/charts?year=${year}&month=${month}`).then((r) => setCharts(r.data)).catch(() => {});
  }, [year, month, isBM]);

  return (
    <div className="space-y-6 min-w-0" data-testid="dashboard-page">
      <Toaster position="top-center" richColors />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">
            {isBM ? "Dashboard" : "Dashboard Kehadiran"}
          </h2>
          <p className="text-sm text-muted-foreground">
            Ringkasan {MONTHS[month - 1]} {year}
          </p>
        </div>
        <MonthYearPicker year={year} month={month} setYear={setYear} setMonth={setMonth} />
      </div>

      {user?.must_change_password && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 flex items-center gap-3" data-testid="change-pw-banner">
          <KeyRound className="w-5 h-5 text-rose-500 shrink-0" />
          <p className="text-sm text-rose-700 flex-1">
            Anda masih menggunakan password awal. Sebaiknya ganti password Anda.
          </p>
          <Button size="sm" onClick={() => navigate("/profile")} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white" data-testid="banner-change-pw">
            Ganti Password
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <StatCard icon={School} label="Total Sekolah Aktif" value={data?.total_schools ?? "-"} accent="bg-gtsnavy" testid="stat-schools" />
        <StatCard icon={Users} label="Total Guru Aktif" value={data?.total_teachers ?? "-"} accent="bg-gtsorange" testid="stat-teachers" />
        <StatCard icon={CalendarCheck} label="Total Sesi Mengajar" value={data?.total_sessions ?? "-"} accent="bg-emerald-500" testid="stat-sessions" />
      </div>

      {isBM && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard icon={Wallet} label="Total Teaching Fee" value={fin ? formatRupiah(fin.total_teaching_fee) : "-"} accent="bg-gtsorange" testid="stat-fee" />
          <StatCard icon={Receipt} label="Total Penerimaan" value={fin ? formatRupiah(fin.total_penerimaan) : "-"} accent="bg-emerald-500" testid="stat-income" />
          <StatCard icon={TrendingDown} label="Total Pengeluaran" value={fin ? formatRupiah(fin.total_pengeluaran) : "-"} accent="bg-gtsnavy" testid="stat-expense" />
          <Card className={`rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-5 text-white bg-gradient-to-br ${fin && fin.saldo_bersih < 0 ? "from-rose-500 to-rose-600" : fin && fin.saldo_bersih === 0 ? "from-gtsorange to-gtsorange-light" : "from-emerald-500 to-emerald-600"}`} data-testid="stat-saldo">
            <p className="text-xs text-white/80">Saldo Bersih GTS</p>
            <p className="font-nunito font-extrabold text-2xl mt-1">{fin ? formatRupiah(fin.saldo_bersih) : "-"}</p>
          </Card>
        </div>
      )}

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white" data-testid="chart-sessions">
        <h3 className="font-nunito font-bold text-gtsnavy mb-4">Jumlah Sesi per Guru</h3>
        {data?.sessions_per_teacher?.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.sessions_per_teacher} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0e6da" vertical={false} />
              <XAxis dataKey="teacher" tick={{ fontSize: 11, fill: "#173B67" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#173B67" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(16,42,86,0.12)" }} />
              <Bar dataKey="sessions" fill="#FF8A3D" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground py-12 text-center">
            Belum ada data sesi untuk periode ini. Input absensi terlebih dahulu.
          </p>
        )}
      </Card>

      {isBM && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white" data-testid="chart-fee">
            <h3 className="font-nunito font-bold text-gtsnavy mb-4">Teaching Fee per Guru ({MONTHS[month - 1]})</h3>
            {charts?.fee_per_teacher?.some((d) => d.fee > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.fee_per_teacher} margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e6da" vertical={false} />
                  <XAxis dataKey="teacher" tick={{ fontSize: 11, fill: "#173B67" }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10, fill: "#173B67" }} tickFormatter={(v) => `${v / 1000}k`} />
                  <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(16,42,86,0.12)" }} />
                  <Bar dataKey="fee" name="Teaching Fee" fill="#102A56" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-12 text-center">Belum ada teaching fee untuk periode ini.</p>
            )}
          </Card>

          <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white" data-testid="chart-income-expense">
            <h3 className="font-nunito font-bold text-gtsnavy mb-4">Penerimaan vs Pengeluaran per Bulan ({year})</h3>
            {charts?.monthly_income_expense ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.monthly_income_expense.map((m) => ({ ...m, label: MONTHS[m.month - 1].slice(0, 3) }))} margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e6da" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#173B67" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#173B67" }} tickFormatter={(v) => `${v / 1000000}jt`} />
                  <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(16,42,86,0.12)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="penerimaan" name="Penerimaan" fill="#10b981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#FF8A3D" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-12 text-center">Memuat data...</p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
